import { CdpSession } from "./cdp-session";

/**
 * A Browser Rendering binding. Structural so it accepts both the classic
 * `Fetcher`-typed binding and the newer `BrowserRun` type generated by
 * `wrangler types` — we only ever use its `fetch` surface.
 */
export interface BrowserBinding {
  fetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response>;
}

export interface BrowserTargetInfo {
  id: string;
  type?: string;
  url?: string;
  title?: string;
  description?: string;
  devtoolsFrontendUrl?: string;
  webSocketDebuggerUrl?: string;
}

export interface BrowserSessionInfo {
  sessionId: string;
  targets?: BrowserTargetInfo[];
  webSocketDebuggerUrl?: string;
}

export interface ConnectBrowserOptions {
  timeoutMs?: number;
  keepAliveMs?: number;
  includeTargets?: boolean;
  /**
   * Opt into Browser Run [session recording](https://developers.cloudflare.com/browser-run/features/session-recording/):
   * an rrweb capture of DOM changes, input, and navigation, finalized when the
   * session closes. Set at session-launch time. Retrieve later with
   * {@link getBrowserRecording}.
   */
  recording?: boolean;
}

/** An rrweb session recording for a closed Browser Run session. */
export interface BrowserRecording {
  sessionId: string;
  /** Recording length in milliseconds. */
  duration: number;
  /**
   * rrweb event arrays keyed by CDP target id (one per tab). Each value can be
   * passed directly to `rrweb-player` to replay that tab.
   */
  events: Record<string, unknown[]>;
}

export class BrowserRenderingError extends Error {
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
    this.name = "BrowserRenderingError";
  }
}

function browserSessionEndpoint(
  sessionId?: string,
  options?: {
    keepAliveMs?: number;
    includeTargets?: boolean;
    recording?: boolean;
  }
): string {
  const path = sessionId
    ? `/v1/devtools/browser/${sessionId}`
    : "/v1/devtools/browser";
  const url = new URL(`https://localhost${path}`);

  if (options?.keepAliveMs !== undefined) {
    url.searchParams.set("keep_alive", String(options.keepAliveMs));
  }
  if (options?.includeTargets) {
    url.searchParams.set("targets", "true");
  }
  if (options?.recording) {
    url.searchParams.set("recording", "true");
  }

  return url.toString();
}

async function parseBrowserSessionInfo(
  response: Response
): Promise<BrowserSessionInfo> {
  const payload = (await response.json()) as {
    sessionId?: unknown;
    targets?: unknown;
    webSocketDebuggerUrl?: unknown;
  };
  const sessionId =
    typeof payload.sessionId === "string" ? payload.sessionId : "";

  if (!sessionId) {
    throw new Error("Browser Rendering response did not include a sessionId");
  }

  return {
    sessionId,
    targets: Array.isArray(payload.targets)
      ? (payload.targets as BrowserTargetInfo[])
      : undefined,
    webSocketDebuggerUrl:
      typeof payload.webSocketDebuggerUrl === "string"
        ? payload.webSocketDebuggerUrl
        : undefined
  };
}

export async function createBrowserSession(
  browser: BrowserBinding,
  options?: {
    keepAliveMs?: number;
    includeTargets?: boolean;
    recording?: boolean;
  }
): Promise<BrowserSessionInfo> {
  const response = await browser.fetch(
    browserSessionEndpoint(undefined, options),
    { method: "POST" }
  );

  if (!response.ok) {
    throw new BrowserRenderingError(
      `Failed to create Browser Rendering session: ${response.status}`,
      response.status
    );
  }

  return parseBrowserSessionInfo(response);
}

export async function listBrowserTargets(
  browser: BrowserBinding,
  sessionId: string
): Promise<BrowserTargetInfo[]> {
  const response = await browser.fetch(
    `https://localhost/v1/devtools/browser/${sessionId}/json/list`
  );

  if (!response.ok) {
    throw new BrowserRenderingError(
      `Failed to list Browser Rendering targets for ${sessionId}: ${response.status}`,
      response.status
    );
  }

  const payload = (await response.json()) as unknown;
  return Array.isArray(payload) ? (payload as BrowserTargetInfo[]) : [];
}

export async function deleteBrowserSession(
  browser: BrowserBinding,
  sessionId: string
): Promise<void> {
  const response = await browser.fetch(
    `https://localhost/v1/devtools/browser/${sessionId}`,
    { method: "DELETE" }
  );

  if (!response.ok && response.status !== 404) {
    throw new BrowserRenderingError(
      `Failed to delete Browser Rendering session ${sessionId}: ${response.status}`,
      response.status
    );
  }
}

/**
 * Connect to a Browser Rendering session and delete it when closed.
 */
export async function connectBrowser(
  browser: BrowserBinding,
  options?: number | ConnectBrowserOptions
): Promise<CdpSession> {
  const normalizedOptions =
    typeof options === "number" ? { timeoutMs: options } : (options ?? {});
  const response = await browser.fetch(
    browserSessionEndpoint(undefined, {
      keepAliveMs: normalizedOptions.keepAliveMs,
      includeTargets: normalizedOptions.includeTargets,
      recording: normalizedOptions.recording
    }),
    { headers: { Upgrade: "websocket" } }
  );

  const ws = response.webSocket;
  if (!ws) {
    throw new Error(
      "Browser Rendering binding did not return a WebSocket. " +
        "Ensure the 'browser' binding is configured in wrangler.jsonc."
    );
  }

  const sessionId = response.headers.get("cf-browser-session-id");
  if (!sessionId) {
    throw new Error(
      "Browser Rendering binding did not include a session ID when opening the CDP WebSocket"
    );
  }

  ws.accept();
  return new CdpSession(
    ws,
    normalizedOptions.timeoutMs,
    () => {
      deleteBrowserSession(browser, sessionId).catch((error: unknown) => {
        console.warn(
          `[agents/browser] Failed to delete one-shot Browser Run session ${sessionId}`,
          error
        );
      });
    },
    sessionId
  );
}

/**
 * Fetch a Browser Run [session recording](https://developers.cloudflare.com/browser-run/features/session-recording/)
 * by session id, via the Browser Rendering REST API.
 *
 * A recording is only available **after** the session closes (explicit
 * `closeSession()`, idle `keep_alive` expiry, or sweep) and is retained for 30
 * days. Capture the session id while the session is alive — e.g. from
 * `connector.liveView()`/`sessionInfo()` — then call this once it has ended.
 *
 * Unlike the binding-based helpers, this hits `api.cloudflare.com` directly, so
 * it needs an account id and an API token with `Browser Rendering` read access
 * (the Workers Browser Run binding cannot read recordings).
 */
export async function getBrowserRecording(options: {
  accountId: string;
  apiToken: string;
  sessionId: string;
  /** Override for testing; defaults to the global `fetch`. */
  fetchImpl?: typeof fetch;
}): Promise<BrowserRecording> {
  const { accountId, apiToken, sessionId } = options;
  const fetchImpl = options.fetchImpl ?? fetch;
  const endpoint = `https://api.cloudflare.com/client/v4/accounts/${accountId}/browser-rendering/recording/${sessionId}`;

  const response = await fetchImpl(endpoint, {
    headers: { Authorization: `Bearer ${apiToken}` }
  });

  if (!response.ok) {
    throw new BrowserRenderingError(
      `Failed to fetch Browser Run recording for ${sessionId}: ${response.status}`,
      response.status
    );
  }

  // The recording endpoint returns the recording object directly; tolerate a
  // standard v4 `{ result }` envelope in case that changes.
  const payload = (await response.json()) as
    | BrowserRecording
    | { result?: BrowserRecording };
  const recording =
    "result" in payload && payload.result ? payload.result : payload;

  return recording as BrowserRecording;
}

/**
 * Connect to an existing Browser Rendering session without deleting it on close.
 */
export async function connectBrowserSession(
  browser: BrowserBinding,
  sessionId: string,
  timeoutMs?: number
): Promise<CdpSession> {
  const response = await browser.fetch(browserSessionEndpoint(sessionId), {
    headers: { Upgrade: "websocket" }
  });

  const ws = response.webSocket;
  if (!ws) {
    throw new Error(
      `Browser Rendering binding did not return a WebSocket for session ${sessionId}`
    );
  }

  ws.accept();
  return new CdpSession(ws, timeoutMs, undefined, sessionId);
}
